// Which states hold land on a date (default worldview, ownership from the borders history), so a
// city can be shown in the state that held it: Pristina in 2003 is in Serbia and Montenegro, not in
// Kosovo. A state "loses" land only if it holds some at another date; a city-state too small for a
// hex (Singapore, Monaco) never holds any, so it is never lost.
//
//   hexes of the base 2000 picture ─► + every border change ─► states with land in some picture
//   the picture at D                                          ─► states with land at D
//   lost(iso3, D)  =  has land in some picture  and  none at D
//   heir(iso3, D)  =  the state holding most of iso3's land (as it is in its newest picture) on D
//
// The heir is asked of the whole territory, not of one hex: a border town (Nimule) sits in a hex
// that the picture may give to the neighbour (Uganda) though the town was in the predecessor (Sudan).

import type { Country, DateStr } from '../core/schema.ts';
import { EPOCH_START } from '../core/dates.ts';
import { defaultOwners, type OwnerModel } from './owners.ts';

/** The land on one date. */
export interface Land {
  /** The same object for every date sharing one border picture (a memo key). */
  readonly key: object;
  /** The state holds no land on the date, though it does on another. */
  lost(iso3: string): boolean;
  /** The state that held most of the lost state's land on the date, or null when nothing did. */
  heir(iso3: string): string | null;
}

export type LandOn = (date: DateStr) => Land;

/** The owner ids that hold at least one hex of the array. */
function heldBy(owner: Uint16Array): Set<number> {
  const held = new Set<number>();
  for (let h = 0; h < owner.length; h++) {
    held.add(owner[h]);
  }
  return held;
}

/** The hexes the owner holds in the array. */
function hexesOf(owner: Uint16Array, id: number): number[] {
  const out: number[] = [];
  for (let h = 0; h < owner.length; h++) {
    if (owner[h] === id) {
      out.push(h);
    }
  }
  return out;
}

export function landModel(owners: OwnerModel, countries: Country[]): LandOn {
  const isoOf = new Map(countries.map((c) => [c.id, c.iso3]));
  const idOf = new Map(countries.map((c) => [c.iso3, c.id]));
  const lands = new WeakMap<Uint16Array, Land>();
  let ever: Set<number> | undefined;
  const territories = new Map<number, number[]>();

  // Every picture is one of the base or the result of one change: each is built once.
  function everHeld(): Set<number> {
    ever ??= new Set([EPOCH_START, ...owners.changes.map((c) => c.date)].flatMap((d) => [...heldBy(defaultOwners(owners, d))]));
    return ever;
  }

  // The state's hexes in the newest picture that gives it any.
  function territory(id: number): number[] {
    const known = territories.get(id);
    if (known) {
      return known;
    }

    const dates = [EPOCH_START, ...owners.changes.map((c) => c.date)].reverse();
    const picture = dates.map((d) => defaultOwners(owners, d)).find((o) => heldBy(o).has(id));
    const hexes = picture ? hexesOf(picture, id) : [];
    territories.set(id, hexes);
    return hexes;
  }

  function heirOf(owner: Uint16Array, id: number): string | null {
    const tally = new Map<number, number>();
    for (const h of territory(id)) {
      tally.set(owner[h], (tally.get(owner[h]) ?? 0) + 1);
    }

    const ranked = [...tally].filter(([o]) => o !== id && isoOf.has(o)).sort((a, b) => b[1] - a[1]);
    return ranked.length > 0 ? (isoOf.get(ranked[0][0]) ?? null) : null;
  }

  function make(owner: Uint16Array): Land {
    const held = heldBy(owner);
    const heirs = new Map<number, string | null>();
    return {
      key: owner,
      lost: (iso3) => {
        const id = idOf.get(iso3);
        return id !== undefined && !held.has(id) && everHeld().has(id);
      },
      heir: (iso3) => {
        const id = idOf.get(iso3);
        if (id === undefined) {
          return null;
        }

        const known = heirs.get(id);
        if (known !== undefined) {
          return known;
        }

        const found = heirOf(owner, id);
        heirs.set(id, found);
        return found;
      },
    };
  }

  return (date) => {
    const owner = defaultOwners(owners, date);
    const hit = lands.get(owner);
    if (hit) {
      return hit;
    }

    const made = make(owner);
    lands.set(owner, made);
    return made;
  };
}
